import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Plus, 
  Trash2, 
  Save, 
  Image as ImageIcon, 
  Eye,
  RefreshCw
} from 'lucide-react';
import MediaSelector from '@/components/admin/MediaSelector';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAGES = [
  { slug: 'home', name: 'Portada (Home)', icon: '🏠' },
  { slug: 'viajes', name: 'Listado Viajes', icon: '✈️' },
  { slug: 'destinos', name: 'Listado Destinos', icon: '🌍' },
  { slug: 'sobre-nosotros', name: 'Sobre Nosotros', icon: '👥' },
  { slug: 'servicios-empresas', name: 'Viajes Empresa', icon: '💼' },
  { slug: 'contacto', name: 'Contacto', icon: '📧' },
  { slug: 'blog', name: 'Blog', icon: '📝' },
  { slug: 'guias-personalizadas', name: 'Guías Personalizadas', icon: '🗺️' },
  { slug: 'recursos-gratuitos', name: 'Recursos Gratis', icon: '🎁' }
];

export default function AdminPages() {
  const [selectedPage, setSelectedPage] = useState('sobre-nosotros');
  const [pageData, setPageData] = useState({
    title: '',
    subtitle: '',
    hero_image: '',
    sections: [],
    meta_description: ''
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [showMediaSelector, setShowMediaSelector] = useState(null); // {type: 'hero' | 'section', index: number}

  useEffect(() => {
    loadPageContent(selectedPage);
    loadMediaFiles();
  }, [selectedPage]);

  const loadPageContent = async (pageSlug) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/pages/${pageSlug}?t=${Date.now()}`);
      console.log('📄 Page content loaded:', res.data);
      setPageData({
        title: res.data.title || '',
        subtitle: res.data.subtitle || '',
        hero_image: res.data.hero_image || '',
        sections: res.data.sections || [],
        meta_description: res.data.meta_description || ''
      });
    } catch (err) {
      console.error('Error loading page:', err);
      toast.error('Error al cargar la página');
    } finally {
      setLoading(false);
    }
  };

  const loadMediaFiles = async () => {
    try {
      const res = await axios.get(`${API}/media`);
      setMediaFiles(res.data.filter(m => m.file_type === 'image'));
    } catch (err) {
      console.error('Error loading media:', err);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const token = localStorage.getItem('admin_token');
    
    try {
      await axios.put(
        `${API}/pages/${selectedPage}`,
        pageData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('✅ Página guardada correctamente');
      await loadPageContent(selectedPage); // Reload to confirm
    } catch (err) {
      console.error('Error saving page:', err);
      toast.error('Error al guardar la página');
    } finally {
      setSaving(false);
    }
  };

  const addSection = () => {
    setPageData(prev => ({
      ...prev,
      sections: [
        ...prev.sections,
        { title: '', content: '', image: '' }
      ]
    }));
  };

  const removeSection = (index) => {
    setPageData(prev => ({
      ...prev,
      sections: prev.sections.filter((_, i) => i !== index)
    }));
  };

  const updateSection = (index, field, value) => {
    setPageData(prev => ({
      ...prev,
      sections: prev.sections.map((section, i) => 
        i === index ? { ...section, [field]: value } : section
      )
    }));
  };

  const selectImage = (imageUrl) => {
    if (showMediaSelector?.type === 'hero') {
      setPageData(prev => ({ ...prev, hero_image: imageUrl }));
    } else if (showMediaSelector?.type === 'section') {
      updateSection(showMediaSelector.index, 'image', imageUrl);
    }
    setShowMediaSelector(null);
  };

  const resetPage = async () => {
    if (!window.confirm('¿Resetear esta página a los valores por defecto?')) return;
    
    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(`${API}/pages/${selectedPage}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Página reseteada');
      await loadPageContent(selectedPage);
    } catch (err) {
      console.error('Error resetting page:', err);
      toast.error('Error al resetear la página');
    }
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Gestión de Páginas</h1>
            <p className="text-gray-600">
              Edita el contenido, imágenes y textos de cada página de la web
            </p>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={resetPage}
              className="gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Resetear Página
            </Button>
            <Button 
              onClick={handleSave}
              disabled={saving}
              className="gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </div>
        </div>

        <Tabs value={selectedPage} onValueChange={setSelectedPage}>
          <TabsList className="mb-6">
            {PAGES.map(page => (
              <TabsTrigger key={page.slug} value={page.slug} className="gap-2">
                <span>{page.icon}</span>
                {page.name}
              </TabsTrigger>
            ))}
          </TabsList>

          {PAGES.map(page => (
            <TabsContent key={page.slug} value={page.slug}>
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Basic Info */}
                  <Card className="p-6">
                    <h3 className="text-lg font-semibold mb-4">Información Básica</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Título de la Página
                        </label>
                        <Input
                          value={pageData.title}
                          onChange={(e) => setPageData(prev => ({ ...prev, title: e.target.value }))}
                          placeholder="Ej: Sobre Nosotros"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Subtítulo
                        </label>
                        <Input
                          value={pageData.subtitle}
                          onChange={(e) => setPageData(prev => ({ ...prev, subtitle: e.target.value }))}
                          placeholder="Ej: Tu agencia de viajes de confianza"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Meta Descripción (SEO)
                        </label>
                        <Textarea
                          value={pageData.meta_description}
                          onChange={(e) => setPageData(prev => ({ ...prev, meta_description: e.target.value }))}
                          placeholder="Descripción breve para motores de búsqueda"
                          rows={2}
                        />
                      </div>
                    </div>
                  </Card>

                  {/* Hero Image */}
                  <Card className="p-6">
                    <h3 className="text-lg font-semibold mb-4">Imagen Hero</h3>
                    <div className="flex items-center gap-4">
                      {pageData.hero_image ? (
                        <div className="relative">
                          <img
                            src={`${process.env.REACT_APP_BACKEND_URL}${pageData.hero_image}`}
                            alt="Hero"
                            className="w-40 h-24 object-cover rounded"
                          />
                          <button
                            onClick={() => setPageData(prev => ({ ...prev, hero_image: '' }))}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <div className="w-40 h-24 bg-gray-100 rounded flex items-center justify-center">
                          <ImageIcon className="w-8 h-8 text-gray-400" />
                        </div>
                      )}
                      <Button
                        variant="outline"
                        onClick={() => setShowMediaSelector({ type: 'hero' })}
                        className="gap-2"
                      >
                        <ImageIcon className="w-4 h-4" />
                        Seleccionar Imagen
                      </Button>
                    </div>
                  </Card>

                  {/* Sections */}
                  <Card className="p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold">Secciones de Contenido</h3>
                      <Button onClick={addSection} variant="outline" size="sm" className="gap-2">
                        <Plus className="w-4 h-4" />
                        Añadir Sección
                      </Button>
                    </div>

                    {pageData.sections.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">
                        No hay secciones. Añade una para empezar.
                      </p>
                    ) : (
                      <div className="space-y-6">
                        {pageData.sections.map((section, index) => (
                          <div key={index} className="border rounded-lg p-4 bg-gray-50">
                            <div className="flex justify-between items-start mb-4">
                              <Badge>Sección {index + 1}</Badge>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeSection(index)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>

                            <div className="space-y-4">
                              <div>
                                <label className="block text-sm font-medium mb-2">
                                  Título de Sección
                                </label>
                                <Input
                                  value={section.title}
                                  onChange={(e) => updateSection(index, 'title', e.target.value)}
                                  placeholder="Ej: Nuestra Historia"
                                />
                              </div>

                              <div>
                                <label className="block text-sm font-medium mb-2">
                                  Contenido
                                </label>
                                <Textarea
                                  value={section.content}
                                  onChange={(e) => updateSection(index, 'content', e.target.value)}
                                  placeholder="Texto de la sección..."
                                  rows={4}
                                />
                              </div>

                              <div>
                                <label className="block text-sm font-medium mb-2">
                                  Imagen de Sección
                                </label>
                                <div className="flex items-center gap-4">
                                  {section.image ? (
                                    <div className="relative">
                                      <img
                                        src={`${process.env.REACT_APP_BACKEND_URL}${section.image}`}
                                        alt="Section"
                                        className="w-32 h-20 object-cover rounded"
                                      />
                                      <button
                                        onClick={() => updateSection(index, 'image', '')}
                                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="w-32 h-20 bg-gray-100 rounded flex items-center justify-center">
                                      <ImageIcon className="w-6 h-6 text-gray-400" />
                                    </div>
                                  )}
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setShowMediaSelector({ type: 'section', index })}
                                    className="gap-2"
                                  >
                                    <ImageIcon className="w-3 h-3" />
                                    Seleccionar
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </Card>
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>

      {/* Media Selector Modal */}
      {showMediaSelector && (
        <MediaSelector
          onSelect={(url) => {
            selectImage(url);
            setShowMediaSelector(null);
          }}
          onClose={() => setShowMediaSelector(null)}
        />
      )}
      </div>
    </AdminLayout>
  );
}
